import React, { useState, useEffect } from 'react';

const Taskbar = ({ onStartClick, openWindows = [], onRestoreWindow, onToggleMinimize, getWindowTitle, getWindowIcon }) => {
  const [isStartHovered, setIsStartHovered] = useState(false);
  const [isStartPressed, setIsStartPressed] = useState(false);
  const [currentTime, setCurrentTime] = useState('');

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const timeString = now.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
      setCurrentTime(timeString);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // Debug: log when openWindows changes
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('Taskbar openWindows:', openWindows);
  }, [openWindows]);

  const handleStartClick = (e) => {
    e.stopPropagation();
    console.log('Start button clicked!');
    setIsStartPressed(!isStartPressed);
    onStartClick();
  };

  return (
    <div id="taskbar" className="flex absolute bottom-0 w-full h-[30px] z-[1002]" style={{ borderTop: 'none' }}>
      {/* Taskbar Background */}
      <div className="background sliced vertical absolute w-full h-full z-0 pointer-events-none">
        <div className="row middle flex-grow"></div>
        <div className="row bottom h-[11px]"></div>
      </div>

      {/* Start Button */}
      <div 
        id="start-button" 
        className={`flex-shrink-0 relative z-10 ${isStartHovered ? 'hover' : ''} ${isStartPressed ? 'pressed' : ''}`}
        onMouseEnter={() => setIsStartHovered(true)}
        onMouseLeave={() => setIsStartHovered(false)}
        onClick={handleStartClick}
      >
        <div className="container relative overflow-hidden">
          <div className="background sliced absolute w-full h-full">
            <div className="column left w-[6px] flex flex-col">
              <div className="row middle flex-grow"></div>
              <div className="row bottom h-[14px]"></div>
            </div>
            <div className="column middle flex flex-col flex-grow">
              <div className="row middle flex-grow"></div>
              <div className="row bottom h-[14px]"></div>
            </div>
            <div className="column right w-[52px] flex flex-col">
              <div className="row middle flex-grow"></div>
              <div className="row bottom h-[14px]"></div>
            </div>
          </div>
          <div className="content flex items-center relative mx-[10px] my-[2px] mr-[24px] mb-[4px] h-[26px] pointer-events-none">
            <span className="icon">
              <img 
                src="https://raw.githubusercontent.com/tdcosta100/WindowsXP/master/images/System/WindowsLogo-small.png" 
                alt="Windows Logo"
                className="block"
                style={{ imageRendering: 'pixelated' }}
              />
            </span>
            <span className="text text-white font-franklin-gothic italic text-[14pt] ml-1 mb-[1px] pr-[3px]" 
                  style={{ 
                    textShadow: '1px 2px 2px rgb(69, 76, 16), 0 0 3px rgb(69, 76, 16)',
                    fontFamily: 'Franklin Gothic Medium, sans-serif'
                  }}>
              start
            </span>
          </div>
        </div>
      </div>

      {/* Task Band */}
      <div id="task-band" className="flex-grow flex items-center px-2 space-x-2 relative z-10 overflow-x-auto">
        {openWindows.map((window) => (
          <div
            key={window.id}
            role="button"
            tabIndex={0}
            aria-label={`Taskbar button for ${getWindowTitle(window.type)} (${window.isMinimized ? 'minimized' : 'open'})`}
            className={`flex items-center px-2 py-[2px] min-w-[140px] max-w-[220px] h-[24px] cursor-pointer border rounded-sm shadow ${
              window.isMinimized 
                ? 'bg-[#e7f0fe] border-[#8fb2e3]' 
                : 'bg-[#cfe5ff] border-[#7aa5e6]'
            } hover:bg-[#dbeaff] focus:outline-none focus:ring-2 focus:ring-blue-500`}
            onClick={() => {
              if (window.isMinimized) {
                onRestoreWindow && onRestoreWindow(window.id);
                return;
              }
              onToggleMinimize && onToggleMinimize(window.id);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (window.isMinimized) {
                  onRestoreWindow && onRestoreWindow(window.id);
                  return;
                }
                onToggleMinimize && onToggleMinimize(window.id);
              }
            }}
            style={{
              borderRadius: '2px',
              fontSize: '11px',
              fontFamily: 'Tahoma, sans-serif'
            }}
          >
            <img 
              src={getWindowIcon(window.type)} 
              alt=""
              className="w-4 h-4 mr-2 flex-shrink-0"
              style={{ imageRendering: 'pixelated' }}
            />
            <span className="truncate text-black text-[11px] leading-none">
              {getWindowTitle(window.type)}
            </span>
          </div>
        ))}
      </div>

      {/* Tray Notify */}
      <div id="tray-notify" className="flex-shrink-0 relative z-10">
        <div className="container relative overflow-hidden h-full">
          <div className="background sliced absolute w-full h-full">
            <div className="column left w-[34px] flex flex-col">
              <div className="row middle flex-grow"></div>
              <div className="row bottom h-[12px]"></div>
            </div>
            <div className="column middle flex flex-col flex-grow">
              <div className="row middle flex-grow"></div>
              <div className="row bottom h-[12px]"></div>
            </div>
            <div className="column right w-[10px] flex flex-col">
              <div className="row middle flex-grow"></div>
              <div className="row bottom h-[12px]"></div>
            </div>
          </div>
          <div className="content flex flex-row-reverse items-center relative mx-[19px] my-0 mr-[7px] h-[30px] text-white text-[8pt]"
               style={{ fontFamily: 'Tahoma, sans-serif' }}>
            <div className="clock w-[40px] text-center relative h-full flex items-center justify-center">
              <div className="text">{currentTime}</div>
              <div className="overlay absolute left-0 top-0 w-full h-full" title="Monday, April 8, 2019"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Taskbar;
